import { useState } from "react";
import { Download, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
    AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "../ui/alert-dialog";

export default function DataCard() {
    const { logout } = useAuth();
    const [exporting, setExporting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [pw, setPw] = useState("");
    const [deleting, setDeleting] = useState(false);

    const exportData = async () => {
        setExporting(true);
        try {
            const { data } = await api.get("/auth/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `cadence-export-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Your data was downloaded");
        } catch {
            toast.error("Couldn't export your data");
        } finally {
            setExporting(false);
        }
    };

    const confirmDelete = async () => {
        if (!pw) return toast.error("Enter your password to confirm");
        setDeleting(true);
        try {
            await api.delete("/auth/me", { data: { password: pw } });
            toast.success("Account deleted. Sorry to see you go.");
            logout();
            window.location.href = "/auth";
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete account");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="settings-data">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">Privacy & data</div>

            <div className="mt-4 flex items-start justify-between gap-3">
                <div>
                    <div className="font-medium">Export your data</div>
                    <div className="text-xs text-stone-500 mt-0.5">A JSON file with your profile, sessions, and goals.</div>
                </div>
                <Button onClick={exportData} disabled={exporting} variant="outline" className="rounded-full shrink-0" data-testid="export-data">
                    <Download size={14} className="mr-1.5" /> {exporting ? "Exporting…" : "Export"}
                </Button>
            </div>

            <div className="mt-6 pt-5 border-t border-stone-200 dark:border-stone-800 flex items-start justify-between gap-3">
                <div>
                    <div className="font-medium text-red-700 dark:text-red-400">Delete account</div>
                    <div className="text-xs text-stone-500 mt-0.5">Permanently removes your data. There's no undo.</div>
                </div>
                <AlertDialog open={deleteOpen} onOpenChange={(v) => { setDeleteOpen(v); if (!v) setPw(""); }}>
                    <AlertDialogTrigger asChild>
                        <Button variant="outline" className="rounded-full shrink-0 border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-950" data-testid="delete-account-open">
                            <Trash2 size={14} className="mr-1.5" /> Delete
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-3xl" data-testid="delete-account-dialog">
                        <AlertDialogHeader>
                            <div className="flex items-center gap-2 text-red-600 text-[11px] tracking-[0.2em] uppercase font-bold">
                                <AlertTriangle size={12} /> This is permanent
                            </div>
                            <AlertDialogTitle className="font-serif text-2xl font-light tracking-tight">Delete your Cadence account?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Your profile, all sessions, recordings, goals, and reminders will be gone forever. Type your password to confirm.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="mt-3">
                            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">Password</Label>
                            <Input
                                type="password"
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                className="mt-2 rounded-2xl"
                                data-testid="delete-account-password"
                                autoFocus
                            />
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full" data-testid="delete-account-cancel">Cancel</AlertDialogCancel>
                            <Button
                                variant="destructive"
                                onClick={confirmDelete}
                                disabled={deleting || !pw}
                                className="rounded-full"
                                data-testid="delete-account-confirm"
                            >
                                {deleting ? "Deleting…" : "Delete forever"}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    );
}
